import React from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { HelmetProvider } from 'react-helmet-async';
import { Routes, Route, Navigate } from 'react-router-dom';

import { queryClient } from './lib/query-client';

import { AppProvider } from './contexts/AppContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { WebSocketProvider } from './contexts/WebSocketContext';

import Layout from './components/Layout';
import { HelpProvider } from './components/help';
import ProtectedRoute from './components/ProtectedRoute';
import { ToasterComponent } from './components/ui/toaster';
import { UserSettingsSync } from './components/UserSettingsSync';

import Dashboard from './pages/Dashboard';
import BlogAdminPage from './pages/BlogAdminPage';
import BlogPage from './pages/BlogPage';
import CheckoutReturn from './pages/CheckoutReturn';
import Contact from './pages/Contact';
import CreatingPlan from './pages/CreatingPlan';
import GitHubCallback from './pages/GitHubCallback';
import Landing from './pages/Landing';
import NotFound from './pages/NotFound';
import OAuthCallback from './pages/OAuthCallback';
import OAuthCallbackSecure from './pages/OAuthCallbackSecure';
import Pricing from './pages/Pricing';
import PrivacyPolicy from './pages/PrivacyPolicy';
import IdeaCreate from './pages/IdeaCreate';
import Ideas from './pages/Ideas';
import IdeaView from './pages/IdeaView';
import PlanView from './pages/PlanView';
import ChatPage from './pages/ChatPage';
import Settings from './pages/Settings';
import TermsOfService from './pages/TermsOfService';
import Unsubscribe from './pages/Unsubscribe';

import { tokens } from './design-system';

// Import design system styles
import './design-system/styles/globals.css';

if (process.env.POSTHOG_API_KEY) {
  posthog.init(process.env.POSTHOG_API_KEY, {
    api_host: 'https://us.i.posthog.com',
    defaults: { plan: 'free' },
  });
}

// AuthRoute component to conditionally render Landing or Dashboard based on auth status
const AuthRoute = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: tokens.colors.dark.bg,
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: `4px solid ${tokens.colors.dark.border}`,
          borderTop: `4px solid ${tokens.colors.primary[500]}`,
          borderRadius: '9999px',
          animation: 'spin 1s linear infinite',
        }} />
      </div>
    );
  }

  if (isAuthenticated()) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Landing />;
};

function App() {
  // Check if we have a successful OAuth login flag
  React.useEffect(() => {
    const oauthLoginSuccess = sessionStorage.getItem('oauth_login_success');
    const redirectPath = sessionStorage.getItem('redirect_after_reload');

    if (oauthLoginSuccess === 'true' && redirectPath) {
      sessionStorage.removeItem('oauth_login_success');
      sessionStorage.removeItem('redirect_after_reload');
    }
  }, []);

  return (
    <HelmetProvider>
      <PostHogProvider client={posthog}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <SubscriptionProvider>
            <SettingsProvider>
              <UserSettingsSync />
              <ThemeProvider>
                <AppProvider>
                  <WebSocketProvider>
                    <HelpProvider>
                      <Layout>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<AuthRoute />} />
              <Route path="/landing" element={<Landing />} />
              <Route path="/login" element={<Navigate to="/" replace />} />
              <Route path="/oauth-callback" element={<OAuthCallbackSecure />} />
              <Route path="/oauth/:provider/callback" element={<OAuthCallback />} />
              <Route path="/github/callback" element={<GitHubCallback />} />
              <Route path="/404" element={<NotFound />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/checkout/return" element={
                <ProtectedRoute>
                  <CheckoutReturn />
                </ProtectedRoute>
              } />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPage />} />

              {/* Protected routes */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/ideas" element={
                <ProtectedRoute>
                  <Ideas />
                </ProtectedRoute>
              } />
              <Route path="/ideas/create" element={
                <ProtectedRoute>
                  <IdeaCreate />
                </ProtectedRoute>
              } />
              <Route path="/ideas/:ideaId" element={
                <ProtectedRoute>
                  <IdeaView />
                </ProtectedRoute>
              } />
              <Route path="/tasks/create" element={<Navigate to="/plans/create" replace />} />
              <Route path="/tasks/:taskId" element={<Navigate to="/plans" replace />} />
              <Route path="/plans/creating" element={
                <ProtectedRoute>
                  <CreatingPlan />
                </ProtectedRoute>
              } />
              <Route path="/plans/:taskId" element={
                <ProtectedRoute>
                  <PlanView />
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              } />
              <Route path="/blog/admin" element={
                <ProtectedRoute>
                  <BlogAdminPage />
                </ProtectedRoute>
              } />
              <Route path="/ideas/:ideaId/chat" element={
                <ProtectedRoute>
                  <ChatPage />
                </ProtectedRoute>
              } />
              <Route path="/plans/:taskId/chat" element={
                <ProtectedRoute>
                  <ChatPage />
                </ProtectedRoute>
              } />

              {/* Catch-all */}
              <Route path="*" element={<Navigate to="/404" replace />} />
            </Routes>
                      </Layout>
                    </HelpProvider>
                  </WebSocketProvider>
                </AppProvider>
              </ThemeProvider>
          </SettingsProvider>
            </SubscriptionProvider>
        </AuthProvider>
        <ToasterComponent />
        <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </PostHogProvider>
    </HelmetProvider>
  );
}

export default App;
